import moment from 'moment';
import { ChangeDetectionStrategy, Component, input, Input, InputSignal, Signal } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap, timer } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

const momentConstructor: (value?: any) => moment.Moment = (<any>moment).default || moment;

@Component({
  selector: 'time-ago',
  templateUrl: './time-ago.html',
  styleUrls: ['./time-ago.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TimeAgoComponent {
  // Input / Output
  readonly timeSignal: InputSignal<string> = input.required({ alias: 'time' });
  @Input() extraClass;
  @Input() extra: boolean;

  @Input() isCard: boolean = false;
  @Input() isComment: boolean = false;
  @Input() isChatComment: boolean = false;
  @Input() isThread: boolean = false;
  @Input() isFromSearch: boolean = false;
  @Input() isOnNewMessagesIndicator: boolean = false;

  protected readonly textSignal: Signal<string> = toSignal(
    toObservable(this.timeSignal).pipe(
      switchMap(time => {
        return timer(60000, 5000).pipe(
          startWith(undefined),
          map(() => {
            return this.getTimeText(time);
          })
        );
      })
    )
  );

  /**
   * If used on Cards
   * FOURTH-1373
   *
   * CARDS: (Streams, Folders, Drafts, Trash)
   * Since 00:00 that day, format = "07:20"
   * Anytime during the previous 7 days, format = "Wed"
   * Anytime prior to the previous 7 days, within this calendar year, format = "10 May 12:19"
   * Anytime prior to the previous 7 days, prior to this calendar year, format = "10 May 2016 12:19"
   * @param {string} time
   */
  getCardsTimeFormat(time: string) {
    let isToday = moment(time).isSame(new Date(), 'day');
    let isLast7Days = moment(time).diff(moment(), 'day') > -7;
    let isSameYear = moment(time).isSame(new Date(), 'year');
    if (isToday) {
      return moment(time).format('HH:mm');
    } else if (isLast7Days) {
      return moment(time).format('ddd');
    } else if (isSameYear) {
      return moment(time).format('D MMM HH:mm');
    } else {
      return moment(time).format('D MMM YYYY HH:mm');
    }
  }

  getThreadTimeFormat(time: string) {
    let isToday = moment(time).isSame(new Date(), 'day');
    let isYesterday = moment(time).isSame(moment().add(-1, 'day'), 'day');
    let isLast7Days = moment(time).diff(moment(), 'day') > -7;
    let isSameYear = moment(time).isSame(new Date(), 'year');
    if (isToday) {
      return moment(time).format('HH:mm');
    } else if (isYesterday) {
      return 'Yesterday ';
    } else if (isLast7Days) {
      return moment(time).format('ddd');
    } else if (isSameYear) {
      return moment(time).format('D MMM');
    } else {
      return moment(time).format('D MMM YYYY');
    }
  }

  /**
   * If used on Comments
   * FOURTH-1373
   *
   * COMMENTS:
   * Since 00:00 that day, format = "07:20"
   * Anytime during the previous 7 days, format = "Wed 07:20"
   * If within the last 364 days, format = "10 May 12:19"
   * If prior to the last 364 days, format = "10 May 2016 12:19"
   * Week-day formats are: "Mon / Tue / Wed / Thu / Fri / Sat / Sun"
   * @param {string} time
   */
  getCommentTimeFormat(time) {
    let isToday = moment(time).isSame(new Date(), 'day');
    let isLast7Days = moment(time).diff(moment(), 'day') > -7;
    let isSameYear = moment(time).isSame(new Date(), 'year');
    if (isToday) {
      return moment(time).format('HH:mm');
    } else if (isLast7Days) {
      return moment(time).format('ddd HH:mm');
    } else if (isSameYear) {
      return moment(time).format('D MMM HH:mm');
    } else {
      return moment(time).format('D MMM YYYY HH:mm');
    }
  }

  getChatCommentTimeFormat(time) {
    return moment(time).format('HH:mm');
  }

  getTimeText(time: string): string {
    let now = moment();
    let diff = now.diff(moment(time), 'seconds');
    if (diff < 60 && !this.isThread && !this.isOnNewMessagesIndicator) {
      return 'just now';
    } else if (this.isThread) {
      return this.getThreadTimeFormat(time);
    } else if (this.isCard) {
      return this.getCardsTimeFormat(time);
    } else if (this.isComment) {
      return this.getCommentTimeFormat(time);
    } else if (this.isChatComment) {
      return this.getChatCommentTimeFormat(time);
    } else if (this.isOnNewMessagesIndicator) {
      return moment(time).format('HH:mm on MMMM Do');
    } else if (this.isFromSearch) {
      if (moment(time).isSame(moment(), 'year')) {
        return moment(time).format('DD MMM');
      } else {
        return moment(time).format('DD MMM YY');
      }
    } else if (!this.isCard && this.extra && now.diff(moment(time), 'hours') > 2) {
      return momentConstructor(time).format('MMM Do YYYY, h:mm:ss a');
    } else if (now.diff(moment(time), 'years') > 2000) {
      return '';
    } else {
      return momentConstructor(time).from(momentConstructor());
    }
  }
}
